import { keepPreviousData, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { MAX_PREVIEW_URLS_PER_REQUEST, signCopilotPreviewUrls, type CopilotPreviewUrls } from 'shared/api/copilot'
import logger from 'shared/logger'
import { pause } from 'shared/utils'

const log = logger().child({ namespace: 'copilot/imagePreviewUrls' })

export const COPILOT_PREVIEW_URLS_QUERY_KEY = 'copilot-preview-urls'

/**
 * Signed URLs live for an hour. Refetching well inside that window means the
 * common case never reaches the `<img>` error path at all — that fallback is
 * for a tab left open, not for normal use.
 */
const PREVIEW_URL_STALE_TIME_MS = 30 * 60 * 1000

/**
 * The sign request is a plain read with no side effects, so retrying it costs
 * nothing — a couple of quick attempts absorb the transient blips (a dropped
 * request, a 5xx) that are far more common than Flow actually being down.
 * Not the shared `retryFn`/`retryDelay` convention from `shared/reactQuery`:
 * those gate on a `status_code` that a network-level failure never carries,
 * and their 10s+ delay would leave a thumbnail looking "gone" far longer
 * than this UI-visible read should.
 *
 * Retries live inside the query function, per chunk, rather than on
 * `useQuery`. A query-level retry re-runs every chunk, so one failed batch
 * of 20 would throw away the batches that had already succeeded. The query
 * itself must not retry either: once the inner attempts are exhausted the
 * function returns whatever it could keep, and a second pass would sign
 * those URIs all over again.
 */
const PREVIEW_URL_RETRY_COUNT = 2
const previewUrlRetryDelayMs = (attemptIndex: number): number => Math.min(1000 * 2 ** attemptIndex, 5000)
const EMPTY_FAILED_URIS: readonly string[] = []

/** What this hook stores. `failedUris` is not part of Flow's response. */
interface PreviewUrlQueryData extends CopilotPreviewUrls {
  failedUris: readonly string[]
}

/**
 * URLs already signed for this client, newest write last so a later success
 * wins. The query key includes the whole URI list, so a new image is a new
 * cache entry — the previous entry is the only copy of signatures Flow has
 * not revoked.
 */
const retainedPreviewUrls = (
  queryClient: QueryClient,
  clientId: number,
  uris: readonly string[]
): CopilotPreviewUrls => {
  const urls: Record<string, string> = {}
  const filenames: Record<string, string> = {}
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [COPILOT_PREVIEW_URLS_QUERY_KEY, clientId] })
    .toSorted((left, right) => left.state.dataUpdatedAt - right.state.dataUpdatedAt)

  for (const query of queries) {
    const data = query.state.data as PreviewUrlQueryData | undefined
    if (data == null) continue
    for (const uri of uris) {
      const url = data.urls[uri]
      if (url == null) continue
      urls[uri] = url
      const filename = data.filenames[uri]
      if (filename != null) filenames[uri] = filename
    }
  }

  return { urls, filenames }
}

const chunk = <T>(items: readonly T[], size: number): T[][] => {
  const chunks: T[][] = []
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size))
  }
  return chunks
}

interface PendingBatchSign {
  urls: Record<string, string>
  filenames: Record<string, string>
  failedBatches: string[][]
  lastError: unknown
}

const signBatchesOnce = async (clientId: number, batches: readonly string[][]): Promise<PendingBatchSign> => {
  const urls: Record<string, string> = {}
  const filenames: Record<string, string> = {}
  const failedBatches: string[][] = []
  let lastError: unknown
  const settled = await Promise.allSettled(batches.map(async (batch) => await signCopilotPreviewUrls(clientId, batch)))

  for (let index = 0; index < settled.length; index += 1) {
    const result = settled[index]
    const batch = batches[index]
    if (result == null || batch == null) continue
    if (result.status === 'fulfilled') {
      Object.assign(urls, result.value.urls)
      Object.assign(filenames, result.value.filenames)
      continue
    }
    lastError = result.reason
    failedBatches.push(batch)
  }

  return { urls, filenames, failedBatches, lastError }
}

/**
 * Attempts run one after another: the next one only exists to retry batches
 * the previous one rejected, and the backoff has to sit between them.
 */
const signPendingBatches = async (
  clientId: number,
  batches: string[][],
  attempt: number
): Promise<PendingBatchSign> => {
  if (batches.length === 0 || attempt > PREVIEW_URL_RETRY_COUNT) {
    return { urls: {}, filenames: {}, failedBatches: batches, lastError: undefined }
  }

  if (attempt > 0) await pause(previewUrlRetryDelayMs(attempt - 1))

  const outcome = await signBatchesOnce(clientId, batches)
  const retried = await signPendingBatches(clientId, outcome.failedBatches, attempt + 1)
  return {
    urls: { ...outcome.urls, ...retried.urls },
    filenames: { ...outcome.filenames, ...retried.filenames },
    failedBatches: retried.failedBatches,
    lastError: retried.lastError ?? outcome.lastError
  }
}

const signPreviewUrlChunks = async (
  clientId: number,
  uris: readonly string[],
  queryClient: QueryClient
): Promise<PreviewUrlQueryData> => {
  const signed = await signPendingBatches(clientId, chunk(uris, MAX_PREVIEW_URLS_PER_REQUEST), 0)
  // The sign attempt retries for several seconds. Read retained URLs after
  // it returns so signatures a narrower in-flight query commits during
  // those retries are still available when a URI in this list fails.
  const retained = retainedPreviewUrls(queryClient, clientId, uris)
  const urls = { ...signed.urls }
  const filenames = { ...signed.filenames }
  const failedUris: string[] = []

  for (const uri of signed.failedBatches.flat()) {
    const retainedUrl = retained.urls[uri]
    if (retainedUrl == null) {
      failedUris.push(uri)
      continue
    }
    urls[uri] = retainedUrl
    const retainedFilename = retained.filenames[uri]
    if (retainedFilename != null) filenames[uri] = retainedFilename
  }

  if (Object.keys(urls).length === 0 && failedUris.length > 0) {
    throw signed.lastError instanceof Error ? signed.lastError : new Error('Failed to sign copilot preview urls')
  }

  return { urls, filenames, failedUris }
}

export interface UseCopilotImagePreviewUrlsResult {
  /** Signed HTTPS URL by `gs://` URI. A missing entry means "not signed". */
  previewUrlByGcsUri: Readonly<Record<string, string>>
  /**
   * Original filename by `gs://` URI. A missing entry means the file has no
   * stored record to read a name from — the caller falls back to a generic
   * label, because the URI's last segment is a uuid, not a name.
   */
  filenameByGcsUri: Readonly<Record<string, string>>
  /**
   * True while the batch hasn't yet resolved for the *current* `gcsUris`.
   *
   * Covers both a true first load and a query-key change (switching chats,
   * or loading a thread whose images differ): `keepPreviousData` serves the
   * previous key's data under a synthetic success status while the new
   * key's fetch is still in flight, so the underlying query's own
   * `isLoading` alone goes `false` too early — leaving a not-yet-signed URI
   * indistinguishable from one Flow genuinely omitted.
   */
  isLoading: boolean
  /**
   * True once signing has exhausted its retries and produced no URL at all.
   * A partial failure keeps every URL it still has and lists the rest on
   * {@link failedGcsUris}: raising `isError` here would clear thumbnails
   * that were already on screen. Flow still answers 200 and simply omits a
   * URI it won't sign, so that case never touches this flag or
   * `failedGcsUris`.
   */
  isError: boolean
  /**
   * URIs whose sign request failed and that have no earlier signature to
   * keep showing. Callers render these as retryable errors.
   */
  failedGcsUris: readonly string[]
  /**
   * Re-signs one URI after its URL stopped working; resolves to the new URL.
   *
   * Only for a URI the batch already signed once — it writes the fresh URL
   * into the shared batch cache, merged with whatever else is already there.
   * That merge is only safe once the batch has actually held data for this
   * key: while the batch query is in its error state (`isError`, no data at
   * all), merging into "whatever is cached" merges into nothing, and a
   * `setQueryData` call also flips the whole batch to a successful, mostly
   * empty document — silently turning every *other* still-unsigned image in
   * the same batch into a false "deleted" tile. Use
   * {@link signWithoutCaching} instead for a URI the batch never signed.
   */
  refreshPreviewUrl: (gcsUri: string) => Promise<string | null>
  /**
   * Signs one URI standalone, without touching the shared batch cache.
   *
   * For retrying a single tile after the *batch* sign request failed
   * outright — see {@link refreshPreviewUrl} for why that path must not
   * write through `setQueryData` here. Returns the filename alongside the
   * URL, same as the batch response does, because the caller holds both
   * itself instead of reading `filenameByGcsUri` back off the still-erroring
   * batch — which never has this URI's name to give. A failure leaves the
   * batch's own `isError` as the only signal, so the tile stays a retryable
   * error rather than being marked unavailable off a single retry.
   */
  signWithoutCaching: (gcsUri: string) => Promise<{ url: string; filename?: string } | null>
}

/**
 * Signs the `gs://` URIs of every image in the visible thread so the browser
 * can render them.
 *
 * Flow signs at most `MAX_PREVIEW_URLS_PER_REQUEST` URIs per call, so a long
 * chat is split across several requests and merged back into one map.
 */
export const useCopilotImagePreviewUrls = (
  clientId: number,
  gcsUris: readonly string[]
): UseCopilotImagePreviewUrlsResult => {
  const queryClient = useQueryClient()
  // Sorted + de-duplicated so the query key is stable across re-renders and
  // across messages arriving in a different order.
  const uniqueUris = useMemo(() => Array.from(new Set(gcsUris)).sort((a, b) => a.localeCompare(b)), [gcsUris])

  const { data, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: [COPILOT_PREVIEW_URLS_QUERY_KEY, clientId, uniqueUris],
    queryFn: async ({ client }) => await signPreviewUrlChunks(clientId, uniqueUris, client),
    enabled: Boolean(clientId) && uniqueUris.length > 0,
    retry: false,
    staleTime: PREVIEW_URL_STALE_TIME_MS,
    // A new image widens `uniqueUris`, which is part of the query key, so
    // without this every already-signed thumbnail in the thread would drop
    // its URL and flash back to "loading" the moment one more image is
    // attached — the new key's fetch keeps serving the old key's data until
    // it resolves.
    //
    // That old data is served under a synthetic `status: 'success'`, which
    // is why `isLoading` alone can't gate "still resolving" below — see the
    // `isPlaceholderData` combination a few lines down. Once the fetch
    // fails, placeholder data is dropped; URLs kept past that point come
    // from `retainedPreviewUrls`, written into this key's own result.
    placeholderData: keepPreviousData
  })

  /** One URI's fresh sign result, or `null` on any failure (logged under `logMessage`). */
  const signOne = useCallback(
    async (gcsUri: string, logMessage: string): Promise<{ url: string; filename?: string } | null> => {
      try {
        const signed = await signCopilotPreviewUrls(clientId, [gcsUri])
        const url = signed.urls[gcsUri]
        if (url == null) return null
        return { url, filename: signed.filenames[gcsUri] }
      } catch (error) {
        log.warn({ error: error instanceof Error ? error.message : String(error) }, logMessage)
        return null
      }
    },
    [clientId]
  )

  const refreshPreviewUrl = useCallback(
    async (gcsUri: string): Promise<string | null> => {
      const result = await signOne(gcsUri, 'failed to refresh copilot preview url')
      if (result == null) return null

      // Only safe because this path is for a URI the batch already holds
      // data for — see this function's own doc comment on
      // `UseCopilotImagePreviewUrlsResult` for why `signWithoutCaching`
      // exists instead of reusing this for a URI the batch never signed.
      queryClient.setQueryData<PreviewUrlQueryData>(
        [COPILOT_PREVIEW_URLS_QUERY_KEY, clientId, uniqueUris],
        (existing) => ({
          urls: { ...existing?.urls, [gcsUri]: result.url },
          // The same call returns the name, so leaving a stale one behind
          // would make this hook its own second source of truth.
          filenames: { ...existing?.filenames, ...(result.filename != null ? { [gcsUri]: result.filename } : {}) },
          failedUris: (existing?.failedUris ?? []).filter((uri) => uri !== gcsUri)
        })
      )
      return result.url
    },
    [clientId, queryClient, signOne, uniqueUris]
  )

  const signWithoutCaching = useCallback(
    async (gcsUri: string): Promise<{ url: string; filename?: string } | null> =>
      await signOne(gcsUri, 'failed to sign copilot preview url outside the batch cache'),
    [signOne]
  )

  return {
    previewUrlByGcsUri: data?.urls ?? {},
    filenameByGcsUri: data?.filenames ?? {},
    isLoading: isLoading || isPlaceholderData,
    isError,
    failedGcsUris: data?.failedUris ?? EMPTY_FAILED_URIS,
    refreshPreviewUrl,
    signWithoutCaching
  }
}
